import { StoreWithProps, useClassState } from "@web-monorepo/hooks";
import { useEffect } from "react";
import type { RadioChannel, SendRadioProps } from "src/pods/radio";
import type { ToolkitAction } from "src/state/toolkitReduxStore";
import logEvent from "src/utils/logEvent";

export type UseRadioRemoteProps = {
  displayOpen: (url: string, action?: ToolkitAction) => void;
  displayClose: VoidFunction;
  remoteClose: VoidFunction;
  sendRadio: (params: SendRadioProps) => void;
  channels: RadioChannel[];
  channel?: RadioChannel;
  currentTrackIndex: number;
  playing: boolean;
  isCasting: boolean;
  synced?: boolean;
  /**
   * Injectable [0, 1) random source used to pick the starting track in
   * openDisplay. Defaults to Math.random; tests override to pin the choice.
   */
  getRandomFloat?: () => number;
};

export type UseRadioRemoteResult = {
  channels: RadioChannel[];
  channel?: RadioChannel;
  currentTrackIndex: number;
  playing: boolean;
  isCasting: boolean;
  hasAudioSrc: boolean;
  audioSrc?: string;
  openDisplay: (selectedChannel: RadioChannel) => void;
  closeDisplay: () => void;
  togglePlayback: () => void;
  nextTrack: () => void;
  previousTrack: () => void;
  startDisplayCast: () => void;
  stopDisplayCast: () => void;
};

/**
 * Valtio-backed store for RadioRemote's interaction model. All reactive state
 * lives in the parent toolkit state store (via props); this VM owns lifecycle
 * (mount/unmount log events) and the click handlers that translate UI
 * interactions into displayOpen / displayClose / sendRadio side effects.
 */
class RadioRemoteStore extends StoreWithProps<UseRadioRemoteProps> {
  get hasAudioSrc(): boolean {
    const { channel, currentTrackIndex } = this.props;
    return Boolean(channel && typeof currentTrackIndex === "number" && channel.tracks[currentTrackIndex].src);
  }

  get audioSrc(): string | undefined {
    const { channel, currentTrackIndex } = this.props;
    if (!channel || typeof currentTrackIndex !== "number") return undefined;
    return channel.tracks[currentTrackIndex].src;
  }

  start() {
    logEvent({ eventName: "radio.setup.open" });
  }

  stop() {
    logEvent({ eventName: "radio.setup.close" });
  }

  openDisplay(selectedChannel: RadioChannel) {
    logEvent({ eventName: "radio.start", eventValue: selectedChannel.title.toLowerCase() });
    const random = this.props.getRandomFloat ?? Math.random;
    this.props.sendRadio({
      channel: selectedChannel,
      currentTrackIndex: Math.floor(random() * selectedChannel.tracks.length),
      // If we're casting to a receiver, start playing immediately. Otherwise webview
      // limitations require playback to start from a user touch — leave paused.
      playing: this.props.isCasting,
    });
    this.props.displayOpen("/apps/display/radio", {
      type: "RECEIVE_RADIO",
      payload: { channel: selectedChannel },
    });
  }

  closeDisplay() {
    this.props.displayClose();
    this.props.remoteClose();
    this.props.sendRadio({ channel: null });
  }

  togglePlayback() {
    if (this.props.synced) {
      this.props.sendRadio({
        currentTrackIndex: this.props.currentTrackIndex,
        channel: this.props.channel,
        playing: !this.props.playing,
      });
    }
  }

  nextTrack() {
    const { synced, channel, currentTrackIndex, playing, sendRadio } = this.props;
    if (synced && channel) {
      sendRadio({
        playing,
        channel,
        currentTrackIndex: (currentTrackIndex + 1) % channel.tracks.length,
      });
    }
  }

  previousTrack() {
    const { synced, channel, currentTrackIndex, playing, sendRadio } = this.props;
    if (synced && channel) {
      sendRadio({
        playing,
        channel,
        currentTrackIndex: (currentTrackIndex + (channel.tracks.length - 1)) % channel.tracks.length,
      });
    }
  }

  startDisplayCast() {
    this.props.displayOpen("/apps/display/radio");
  }

  stopDisplayCast() {
    this.props.displayClose();
  }
}

export function useRadioRemote(props: UseRadioRemoteProps): UseRadioRemoteResult {
  const store = useClassState(RadioRemoteStore, props);
  const { start, stop } = store;

  useEffect(() => {
    start();
    return () => stop();
  }, [start, stop]);

  return {
    channels: store.props.channels,
    channel: store.props.channel,
    currentTrackIndex: store.props.currentTrackIndex,
    playing: store.props.playing,
    isCasting: store.props.isCasting,
    hasAudioSrc: store.hasAudioSrc,
    audioSrc: store.audioSrc,
    openDisplay: store.openDisplay,
    closeDisplay: store.closeDisplay,
    togglePlayback: store.togglePlayback,
    nextTrack: store.nextTrack,
    previousTrack: store.previousTrack,
    startDisplayCast: store.startDisplayCast,
    stopDisplayCast: store.stopDisplayCast,
  };
}
